import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AuthContext from "../../contexts/AuthContext/AuthContext";
import "./PaymentSuccess.css";

const PaymentSuccess = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const CLIENT_URL = process.env.REACT_APP_CLIENT_URL;

  const greeting = user?.first_name
    ? `Dear ${user.first_name},`
    : "Dear Customer,";

  useEffect(() => {
    const fetchOrder = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Please sign in to view this page.");
        navigate("/signin");
        return;
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
        const SERVER_URL = process.env.REACT_APP_SERVER_URL;
        const response = await axios.get(
          `${SERVER_URL}/api/orders/${orderId}/`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        clearTimeout(timeoutId);
        setOrder(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching order:", error.response?.data);
        if (error.name === "AbortError") {
          setError("Request timed out. Please try again.");
        } else {
          setError("Failed to load order details.");
        }
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, navigate]);

  if (loading) {
    return (
      <div className="payment-success-loading" role="status" aria-live="polite">
        Loading...
      </div>
    );
  }

  if (!order || error) {
    return (
      <div className="payment-success-error" role="alert" aria-live="assertive">
        <p>{error || "No order data available."}</p>
      </div>
    );
  }

  return (
    <div className="payment-success-container">
      <Helmet>
        <title>Payment Successful – LingoLab</title>
        <meta
          name="description"
          content="Your payment was successful! View your order details and download your translated video from LingoLab."
        />
        <meta name="robots" content="noindex, follow" />
        <link
          rel="canonical"
          href={`https://lingolab.ca/payment-confirmation-page/${orderId}/success`}
        />
        <meta property="og:title" content="Payment Successful – LingoLab" />
        <meta
          property="og:description"
          content="Your payment was successful! View your order details and download your translated video from LingoLab."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://lingolab.ca/payment-success/${orderId}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta name="twitter:title" content="Payment Successful – LingoLab" />
        <meta
          name="twitter:description"
          content="Your payment was successful! View your order details and download your translated video from LingoLab."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div
        className="payment-success-card"
        role="region"
        aria-labelledby="payment-success-title"
      >
        {/* Header */}
        <div className="payment-success-header">
          <h2 className="payment-success-title" id="payment-success-title">
            Payment Successful
          </h2>
        </div>

        {/* Order Details */}
        <div className="payment-success-details">
          <div className="payment-success-field">
            <span className="payment-success-label">Order ID:</span>
            <span>{order.id}</span>
          </div>
          <div className="payment-success-field">
            <span className="payment-success-label">Translation:</span>
            <span>
              {order.video.from_language.name} to {order.video.to_language.name}
            </span>
          </div>
          <div className="payment-success-field">
            <span className="payment-success-label">Original Video:</span>
            <span className="payment-success-url">
              <a
                href={order.video.original_video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="payment-success-link"
                aria-label="View original video"
                onTouchStart={() => {}}
              >
                View Video
              </a>
            </span>
          </div>
          {order.status === "Order Completed" && (
            <div className="payment-success-field">
              <span className="payment-success-label">Download Video:</span>
              <span>
                <a
                  href={order.video.translated_video_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="payment-success-link"
                  aria-label="Download translated video"
                  onTouchStart={() => {}}
                >
                  Download
                </a>
              </span>
            </div>
          )}
          <div className="payment-success-field">
            <span className="payment-success-label">Status:</span>
            <span>{order.status}</span>
          </div>
        </div>

        {/* Success Messages */}
        {order.status === "First Payment Received" && (
          <div className="payment-success-message">
            <h3 className="payment-success-message-title">{greeting}</h3>
            <p>
              Thank you for your order. Your initial payment has been received
              successfully. Our skilled translators are starting work on your
              video right away. We’ll notify you by email when it’s ready, and
              you can check the status at{" "}
              <a
                href={`${CLIENT_URL}/my-orders`}
                className="payment-success-link"
                aria-label="View my orders"
                onTouchStart={() => {}}
              >
                My Orders
              </a>
              . Please check your junk or spam folder if you don’t see the
              notification.
            </p>
          </div>
        )}

        {order.status === "Order Completed" && (
          <div className="payment-success-message">
            <h3 className="payment-success-message-title">{greeting}</h3>
            <p>
              Thank you for your order. Your translated video, from{" "}
              {order.video.from_language.name} to {order.video.to_language.name}
              , is ready to download{" "}
              <a
                href={order.video.translated_video_url}
                className="payment-success-link"
                aria-label="Download translated video"
                onTouchStart={() => {}}
              >
                here
              </a>
              . We’ve sent a confirmation email—please check your junk or spam
              folder if it’s not in your inbox.
            </p>
          </div>
        )}

        {order.status !== "First Payment Received" &&
          order.status !== "Order Completed" && (
            <div className="payment-success-message payment-success-message-error">
              <p>
                Unexpected order status. Please{" "}
                <a
                  href={`${CLIENT_URL}/contact`}
                  className="payment-success-link"
                  aria-label="Contact support"
                  onTouchStart={() => {}}
                >
                  contact support
                </a>
                .
              </p>
            </div>
          )}

        {/* Back to Orders Button */}
        <div className="payment-success-button-container">
          <button
            onClick={() => navigate("/my-orders")}
            onTouchStart={() => {}}
            className="payment-success-button"
            aria-label="Back to my orders"
          >
            Back to My Orders
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
